import { NavLink, Outlet } from 'react-router-dom'
import { Database, KeyRound, Link2, LogOut, ScrollText } from 'lucide-react'

import { cn } from '@/lib/cn'
import { useAuth } from '@/features/auth/AuthProvider'
import { Badge } from './ui/misc'
import { Button } from './ui/button'
import { ThemeToggle } from './ThemeToggle'

const NAV = [
  { to: '/browse', label: 'Files', icon: Database, permission: null },
  { to: '/shares', label: 'Links', icon: Link2, permission: null },
  { to: '/audit', label: 'Audit', icon: ScrollText, permission: null },
  // Hidden without the permission — the page refuses anyway, but offering a
  // link that only leads to a refusal is not useful.
  { to: '/keys', label: 'API keys', icon: KeyRound, permission: 'admin' as const },
]

export function AppShell() {
  const { user, logout, can } = useAuth()

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex shrink-0 items-center justify-between gap-4 border-b border-border px-5 py-2.5">
        <div className="flex items-center gap-6">
          <span className="flex items-center gap-2 font-semibold">
            <Database className="h-4 w-4 text-accent" />
            public-s3-service
          </span>

          <nav className="flex items-center gap-1">
            {NAV.filter((item) => !item.permission || can(item.permission)).map(
              ({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm transition-colors',
                    isActive ? 'bg-muted font-medium' : 'text-muted-foreground hover:bg-muted',
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
              ),
            )}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          {user && (
            <span className="flex items-center gap-2 text-sm">
              <span className="hidden sm:inline" title={user.username}>
                {user.displayName || user.username}
              </span>
              {/* The role is always visible: with one shared HCP account, it is
                  the only thing that decides whether destructive actions are
                  possible. */}
              <Badge
                tone={user.permissions.includes('upload') ? 'accent' : 'neutral'}
                title={`Permissions: ${user.permissions.join(', ') || 'none'}`}
              >
                {user.permissions.includes('upload') ? 'Read/write' : 'Read only'}
              </Badge>
            </span>
          )}
          <ThemeToggle />
          <Button variant="ghost" size="icon" onClick={() => void logout()} aria-label="Sign out" title="Sign out">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div className="min-h-0 flex-1">
        <Outlet />
      </div>
    </div>
  )
}
